import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import shallowEqual from 'shallowequal';

import type { MenuQueryOpts } from '../queries/types';
import { useMenuPickerQuery } from '../queries/use-menu-picker-query';
import { useMenuQueryOpts } from '../queries/use-menu-query-opts';
import type {
  CustomizationSelections,
  MenuPickerAspectOption,
  SelectedCustomization,
} from '../types';
import { forEachCustomizationOptionInPickerAspectOption } from '../utils';

export type MenuPickerContext = {
  slug: string;
  query: ReturnType<typeof useMenuPickerQuery>;

  isLoading?: boolean;
  isComboRequired: boolean;

  // Derived
  displayName: string;
  priceCents: number;

  // Instructions
  instructions: string;
  setInstructions: Dispatch<SetStateAction<string>>;

  // Picker
  pickerSelections: Record<string, string | null>; // Record<PickerAspectId, PickerAspectOptionId>
  selectedCustomizationOptions?: SelectedCustomization[];

  // PickerAspectOption
  selectedPickerAspectOptionId: null | string;
  setSelectedPickerAspectOptionId: (pickerAspectOptionId: string) => void;
  selectedPickerAspectOption: null | MenuPickerAspectOption;
  defaultPickerOption: null | MenuPickerAspectOption;

  // Customizations

  // default customization selections are the selections that bff defines as the default
  defaultCustomizationSelections: CustomizationSelections;
  // user customization selections are the selections the user chose
  userCustomizationSelections: CustomizationSelections;
  setUserCustomizationSelections: Dispatch<SetStateAction<CustomizationSelections>>;
  // resolved customization selections is {...defaults, ...fallbacks, ...userSelections}
  // the resolved customizations are should be what we use to display to the user.
  // This is what they should expect to receive.
  resolvedCustomizationSelections: CustomizationSelections;

  draftCustomizationSelections: CustomizationSelections;
  setDraftCustomizationSelections: Dispatch<SetStateAction<CustomizationSelections>>;

  // Validation
  showErrors: boolean;
  setShowErrors: (shouldShowErrors: boolean) => void;

  // Error messages derived
  // { [customizationOptionKey]: message }
  errorMessages: Record<string, string>;

  hasUnsavedCustomizationSelections: boolean;

  // Used for simply offers to show the customization screen without navigating to a different route
  keepCustomizationInView: boolean;
  shouldShowCustomizationScreen: boolean;
  setShouldShowCustomizationScreen: (shouldShowCustomizationScreen: boolean) => void;
};

const menuPickerContext = createContext<null | MenuPickerContext>(null);

export type MenuPickerProviderProps = {
  children?: ReactNode;
  slug: string;
  menuQueryOpts?: Partial<MenuQueryOpts>;
  keepCustomizationInView?: boolean;
  isInitialStateLoading?: boolean;
  initialState?: {
    selectedPickerAspectOptionId?: string;
    userCustomizationSelections?: CustomizationSelections;
    instructions?: string;
  };
};

const MenuPickerProviderState = (props: MenuPickerProviderProps) => {
  const [shouldShowCustomizationScreen, setShouldShowCustomizationScreen] = useState(false);
  const slug = props.slug;

  const query = useMenuPickerQuery({
    slug,
    menuQueryOpts: useMenuQueryOpts(props.menuQueryOpts),
  });

  const [instructions, setInstructions] = useState(props.initialState?.instructions ?? '');

  // PickerAspectOption
  const [_selectedPickerAspectOptionId, setSelectedPickerAspectOptionId] = useState(
    props.initialState?.selectedPickerAspectOptionId ?? ''
  );
  const defaultPickerAspectOptionId = query.data?.pickerAspect?.defaultOptionKey;
  const defaultPickerOption =
    query.data?.pickerAspect?.options.find(option => option.key === defaultPickerAspectOptionId) ??
    null;
  const selectedPickerAspectOption: MenuPickerContext['selectedPickerAspectOption'] =
    useMemo(() => {
      // if the user select an option
      // We use the default picker aspect option if it's not a picker, meaning it's a menu item, offer item
      if (_selectedPickerAspectOptionId && query.data?._type === 'picker') {
        return (
          query.data?.pickerAspect?.options.find(
            option =>
              option.key === _selectedPickerAspectOptionId ||
              option.id === _selectedPickerAspectOptionId
          ) || null
        );
      }

      // if default option is assigned, check availability
      const searchDefaultPickerAspectOption = query.data?.pickerAspect?.options.find(
        option => option.key === defaultPickerAspectOptionId
      );

      // select default option if it is available
      if (searchDefaultPickerAspectOption && searchDefaultPickerAspectOption?.item?.isAvailable) {
        return searchDefaultPickerAspectOption;
      }

      // if default selection is not available, select the first one.
      return query.data?.pickerAspect?.options.find(option => option.item?.isAvailable) || null;
    }, [
      query.data?._type,
      query.data?.pickerAspect?.options,
      defaultPickerAspectOptionId,
      _selectedPickerAspectOptionId,
    ]);

  const pickerAspectId = query.data?.pickerAspect?.id;
  const selectedPickerAspectOptionId = selectedPickerAspectOption?.id ?? null;

  // Record<PickerAspectId, SelectedPickerAspectOptionId>
  const pickerSelections: Record<string, string | null> = useMemo(() => {
    if (pickerAspectId) {
      return {
        [pickerAspectId]: selectedPickerAspectOptionId,
      };
    }

    return {};
  }, [pickerAspectId, selectedPickerAspectOptionId]);

  // Customizations
  const defaultCustomizationSelections: MenuPickerContext['defaultCustomizationSelections'] =
    useMemo(() => {
      const defaultSelections: MenuPickerContext['defaultCustomizationSelections'] = {};

      forEachCustomizationOptionInPickerAspectOption(
        selectedPickerAspectOption,
        ({ customizationOption }) => {
          if (!customizationOption.key || !customizationOption.defaultOptionKey) {
            return;
          }

          if (
            customizationOption.displayType === 'QUANTITY' &&
            customizationOption.minAmount &&
            customizationOption.defaultOptionKey
          ) {
            defaultSelections[customizationOption.key] = {
              [customizationOption.defaultOptionKey]: customizationOption.minAmount,
            };

            return;
          }

          defaultSelections[customizationOption.key] = customizationOption.defaultOptionKey;
        }
      );

      return defaultSelections;
    }, [selectedPickerAspectOption]);

  const [userCustomizationSelections, setUserCustomizationSelections] = useState<
    MenuPickerContext['userCustomizationSelections']
  >(props.initialState?.userCustomizationSelections ?? {});
  const resolvedCustomizationSelections: MenuPickerContext['resolvedCustomizationSelections'] =
    useMemo(() => {
      return {
        ...defaultCustomizationSelections,
        ...userCustomizationSelections,
      };
    }, [defaultCustomizationSelections, userCustomizationSelections]);

  const [draftCustomizationSelections, setDraftCustomizationSelections] = useState<
    MenuPickerContext['draftCustomizationSelections']
  >({});

  const handlePickerAspectSelection = useCallback(
    (pickerOptionId: string) => {
      setSelectedPickerAspectOptionId(pickerOptionId);

      // Reset customization selections
      setUserCustomizationSelections({});
    },
    [setSelectedPickerAspectOptionId, setUserCustomizationSelections]
  );

  // Price
  const priceCents = useMemo(() => {
    const itemBasePriceCents = selectedPickerAspectOption?.item?.basePriceCents ?? 0;

    // { [optionKey]: { [optionOptionKey]: upchargeCents } }
    const customizationOptionPriceMapping: Record<string, Record<string, number>> = {};
    forEachCustomizationOptionInPickerAspectOption(
      selectedPickerAspectOption,
      ({ customizationOption }) => {
        const keyToPriceMapping: Record<string, number> = {};

        for (const opt of customizationOption.options) {
          keyToPriceMapping[opt.key] = opt.upChargeCents;
        }

        customizationOptionPriceMapping[customizationOption.key] = keyToPriceMapping;
      }
    );

    const customizationsPriceCents = Object.entries(resolvedCustomizationSelections).reduce(
      (cents, [optionKey, optionOption]) => {
        if (typeof optionOption === 'string') {
          // optionOption is a key, not an object of quantities
          return cents + (customizationOptionPriceMapping?.[optionKey]?.[optionOption] ?? 0);
        }

        let optionOptionPriceCents = 0;
        for (const [optionOptionKey, qty] of Object.entries(optionOption)) {
          optionOptionPriceCents +=
            (customizationOptionPriceMapping?.[optionKey]?.[optionOptionKey] ?? 0) * qty;
        }

        return cents + optionOptionPriceCents;
      },
      0
    );

    return itemBasePriceCents + customizationsPriceCents;
  }, [selectedPickerAspectOption, resolvedCustomizationSelections]);

  const selectedCustomizationOptions = useMemo(() => {
    const customizationOptionMapping: Record<string, Record<string, SelectedCustomization>> = {};
    forEachCustomizationOptionInPickerAspectOption(
      selectedPickerAspectOption,
      ({ customizationOption }) => {
        const keyToMapping: Record<string, SelectedCustomization> = {};

        for (const opt of customizationOption.options) {
          keyToMapping[opt.key] = {
            displayName: opt.displayName,
            upChargeCents: opt.upChargeCents,
          };
        }

        customizationOptionMapping[customizationOption.key] = keyToMapping;
      }
    );

    const customizationOptionMappingFiltered = Object.entries(resolvedCustomizationSelections)
      .map(([optionKey, optionOption]) => {
        const defaultOption = defaultCustomizationSelections[optionKey];

        // Only include if the selection is different from default
        if (typeof optionOption === 'string' && optionOption !== defaultOption) {
          // Example:
          // optionKey = "01ca5208124b" (Bacon customization option)
          // optionOption = "0d015d51edd4" (user selected "Bacon" - it could be "No Bacon", for example)
          // This lookup gets the details (like displayName and upChargeCents) for the selected option.
          const selectedOption = customizationOptionMapping?.[optionKey]?.[optionOption];
          if (selectedOption) {
            return {
              displayName: selectedOption.displayName,
              upChargeCents: selectedOption.upChargeCents ?? 0,
            };
          }
        }
        return undefined;
      })
      .filter(Boolean) as SelectedCustomization[];

    return customizationOptionMappingFiltered;
  }, [selectedPickerAspectOption, defaultCustomizationSelections, resolvedCustomizationSelections]);

  // Validation
  const [showErrors, setShowErrors] = useState<MenuPickerContext['showErrors']>(false);
  const errorMessages = useMemo(() => {
    const errors: Record<string, string> = {};

    forEachCustomizationOptionInPickerAspectOption(
      selectedPickerAspectOption,
      ({ customizationOption }) => {
        const selection = resolvedCustomizationSelections[customizationOption.key] ?? {};

        if (typeof selection === 'string') {
          return;
        }

        // Quantities
        const totalQuantity = Object.values(selection).reduce((acc, qty) => acc + qty, 0);
        if (
          (customizationOption.minAmount && totalQuantity < customizationOption.minAmount) ||
          (customizationOption.maxAmount && totalQuantity > customizationOption.maxAmount)
        ) {
          errors[customizationOption.key] = 'Required';
        }
      }
    );

    return errors;
  }, [resolvedCustomizationSelections, selectedPickerAspectOption]);

  const hasUnsavedCustomizationSelections = useMemo(
    () =>
      Object.entries(draftCustomizationSelections).some(
        ([id, selection]) => !shallowEqual(selection, resolvedCustomizationSelections[id])
      ),
    [resolvedCustomizationSelections, draftCustomizationSelections]
  );

  return (
    <menuPickerContext.Provider
      value={{
        slug,
        query,

        isLoading: query.isLoading || props.isInitialStateLoading,

        selectedPickerAspectOptionId,
        setSelectedPickerAspectOptionId: handlePickerAspectSelection,
        instructions,
        setInstructions,
        showErrors,
        setShowErrors,
        keepCustomizationInView: props.keepCustomizationInView ?? false,
        shouldShowCustomizationScreen,
        setShouldShowCustomizationScreen,

        // Customization Selections
        defaultCustomizationSelections,
        userCustomizationSelections,
        setUserCustomizationSelections,
        resolvedCustomizationSelections,

        draftCustomizationSelections,
        setDraftCustomizationSelections,

        // Derived
        pickerSelections,
        selectedPickerAspectOption,
        displayName: query.data?.displayName ?? '',
        isComboRequired: Boolean(query.data?.combo?.isRequired),
        priceCents,
        errorMessages,
        hasUnsavedCustomizationSelections,
        defaultPickerOption,
        selectedCustomizationOptions,
      }}
    >
      {props.children}
    </menuPickerContext.Provider>
  );
};

export const MenuPickerProvider = (props: MenuPickerProviderProps) => {
  // Reset menu picker provider state when slug changes or initialState updates
  return (
    <MenuPickerProviderState
      {...props}
      key={[props.slug, props.isInitialStateLoading].filter(Boolean).join('-')}
    />
  );
};

export const useMenuPicker = (): MenuPickerContext => {
  const context = useContext(menuPickerContext);

  if (!context && __DEV__) {
    throw new Error(
      'useMenuPicker can only be called from within a descendant of MenuPickerProvider'
    );
  }

  return context as MenuPickerContext;
};
